import { uniq } from 'lodash'
import OpacityAnimation from '@/components/animations/OpacityAnimation'
import { Column, MyPieChart, Row, Text } from '@/components/core'
import { classNameBuilder } from '@/helpers'
import { CefrLevel } from '@/services/getFluencyTestResult.ts'

interface WordUsageChartProps {
  data: {
    [x in CefrLevel]: {
      word: string
      cefrLevel: string
      partOfSpeech: string
    }[]
  }
  isResponsive?: boolean
}

const WordUsageChart = ({ data, isResponsive = true }: WordUsageChartProps) => {
  const pieChartData = Object.entries(data)
    .map(([key, values]) => ({
      cefrLevel: key,
      tokens: values.length
    }))
    .filter((result) => validCefrLevels.includes(result.cefrLevel))
    .map((item, index) => ({
      key: item.cefrLevel,
      value: Number(item.tokens),
      fill: variantMap[index]?.pieChartColor || '#E5E5E5'
    }))
    .sort((a, b) => {
      if (a.key < b.key) {
        return -1
      }
      if (a.key > b.key) {
        return 1
      }
      return 0
    })

  const totalAdvancedWords = pieChartData?.reduce(
    (acc, item) => acc + item.value,
    0
  )

  return (
    <Row
      className={classNameBuilder(
        isResponsive && 'max-lg:flex-col max-lg:space-y-8',
        !isResponsive && 'space-y-8'
      )}
    >
      <Column
        className={classNameBuilder(
          isResponsive && 'lg:w-1/2',
          !isResponsive && 'w-1/2'
        )}
      >
        <Column className="relative w-full flex-1 items-center justify-center self-center">
          <MyPieChart data={pieChartData} />
          <OpacityAnimation className="absolute flex w-full flex-col items-center">
            <Text size="4xl" fontWeight="bold">
              {totalAdvancedWords}
            </Text>
            <Text.Translated
              className="shrink-0 text-xs"
              translationKey="advanced_words_app_resultado_components_word_usage_chart"
            />
          </OpacityAnimation>
        </Column>
        <OpacityAnimation className="flex h-fit w-full flex-row flex-wrap items-end justify-center space-x-6">
          {pieChartData?.map((item, index) => (
            <Row key={index} className="items-center space-x-2">
              <div
                className={`h-4 w-4 rounded-full ${
                  variantMap[index]?.color || 'bg-[#E5E5E5]'
                }`}
              />
              <Text size="xs" transform="uppercase">
                {item?.key}
              </Text>
            </Row>
          ))}
        </OpacityAnimation>
      </Column>
      <Column
        className={classNameBuilder(
          'flex items-center justify-center space-y-3',
          isResponsive && 'lg:w-1/2',
          !isResponsive && 'w-1/2'
        )}
      >
        {Object.entries(data)
          .filter(([cefrLevel]) => validCefrLevels.includes(cefrLevel))
          .sort()
          .map(([cefrLevel, values]) => (
            <Row
              key={cefrLevel}
              className="flex h-[60px] w-[90%] items-center justify-between rounded-lg border border-[#E0E0E0] bg-white px-4 py-3.5"
            >
              <Text
                size="xs"
                fontWeight="bold"
                color={'none'}
                className={'text-[#6A0DFD]'}
              >
                {cefrLevel}
              </Text>
              <Text size="xs" color="600">
                {uniq(values.map(({ word }) => word))
                  .slice(0, 3)
                  .join(', ')}
              </Text>
            </Row>
          ))}
      </Column>
    </Row>
  )
}

export default WordUsageChart

const variantMap = [
  {
    color: 'bg-[#C4B5FD]',
    pieChartColor: '#C4B5FD'
  },
  {
    color: 'bg-[#8B5CF6]',
    pieChartColor: '#8B5CF6'
  },
  {
    color: 'bg-[#6D28D9]',
    pieChartColor: '#6D28D9'
  },
  {
    color: 'bg-[#4C1D95]',
    pieChartColor: '#4C1D95'
  }
]

const validCefrLevels = ['B1', 'B1+', 'B2', 'B2+', 'C1', 'C1+', 'C2']
